import { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router';
import { observer } from 'mobx-react';
import { useMount } from '@cutwise/react-utils';
import { logger } from '@cutwise/utils';

import { useAlertStore } from '@/alert/stores/AlertStore';
import { useBillingStore } from '@/billing/stores/BillingStore';
import ErrorBoundary from '@/common/components/ErrorBoundary/ErrorBoundary';
import ErrorChecker from '@/common/components/ErrorChecker/ErrorChecker';
import Header from '@/common/components/Header/Header';
import Loader from '@/common/components/Loader/Loader';
import ErrorPage from '@/common/pages/ErrorPage/ErrorPage';
import AppError from '@/common/state/AppError';
import { useAppStore } from '@/common/stores/AppStore';
import { useConfigurationStore } from '@/common/stores/ConfigurationStore';
import { useContactsStore } from '@/contacts/stores/ContactsStore';
import { E2E_TESTS_ENABLED } from '@/test/constants/e2e';
import AuthService from '@/user/services/AuthService';
import { useUserStore } from '@/user/stores/UserStore';

import styles from './App.module.css';

function App() {
  const { isConfigurationLoaded } = useConfigurationStore();
  const { user, initUserStore, isAuthorized, isBackendUnavailable } = useUserStore();
  const { getAlerts } = useAlertStore();
  const { showTopUpResult } = useBillingStore();

  const { enableCloudSettingsStorage } = useAppStore();
  const { initContactsStore } = useContactsStore();

  useMount(() => {
    AuthService.showNoticeAboutSessionExpirationIfNeeded();

    initUserStore()
      .then(() => showTopUpResult())
      .catch((e) => logger.error(e))
      .finally(() => {
        // make request for Alerts even if fetch user request failed,
        // in this case we reset localStorage and set Anon user,
        // so we will get public Alerts, not personal
        getAlerts().catch((e) => logger.error(e));
      });

    if (E2E_TESTS_ENABLED) {
      document.body.classList.add(styles.e2eMode);
    }
  });

  useEffect(() => {
    if (isAuthorized) {
      enableCloudSettingsStorage();

      initContactsStore();
    }
  }, [isAuthorized]);

  if (isBackendUnavailable) {
    return <ErrorPage error={AppError.createDefaultError()} isReloadPageButtonShown />;
  }

  if (!isConfigurationLoaded || !user) {
    return null;
  }

  return (
    <div className={styles.app}>
      <Header />

      <ErrorBoundary>
        <ErrorChecker>
          <Suspense fallback={<Loader />}>
            <Outlet />
          </Suspense>
        </ErrorChecker>
      </ErrorBoundary>
    </div>
  );
}

export default observer(App);
